import { useState, useSyncExternalStore } from 'react'
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Cargando } from '../../componentes/Cargando'
import { EstadoError } from '../../componentes/EstadoError'
import { EstadoVacio } from '../../componentes/EstadoVacio'
import { IconoGrafica } from '../../componentes/iconos'
import { CLASE_GRAFICA, CSS_PALETA, TINTA } from '../../graficos/paleta'
import { useProductos, type ProductoConsumido } from '../../hooks/useProductos'

const ID_TITULO = 'titulo-productos-mas-consumidos'
const TITULO = 'Productos más consumidos'

/** Alto reservado a cada categoria y grosor de la barra dentro de esa banda. */
const PASO_FILA = 34
const GROSOR_BARRA = 14
/** Banda inferior del eje de valores: si no se reserva, la tarjeta la recorta. */
const ALTO_EJE = 32
const ALTO_MARGENES = 16
/**
 * Ancho del eje de categorias. Los nombres de producto son largos, pero en
 * movil un eje de 200 px se come el area de dibujo y deja las barras en
 * munones: por debajo de 640 px se recorta el eje, no las barras.
 */
const ANCHO_CATEGORIAS = { ancha: 200, estrecha: 104 }
const CARACTERES_VISIBLES = { ancha: 26, estrecha: 13 }
/** Hueco a la derecha para la etiqueta de valor al final de la barra. */
const MARGEN_ETIQUETA = { ancha: 64, estrecha: 44 }
const PUNTO_DE_CORTE = '(min-width: 640px)'

function consultaMedia(): MediaQueryList | undefined {
  return typeof window === 'undefined' ? undefined : window.matchMedia?.(PUNTO_DE_CORTE)
}

/**
 * Ancho de la pantalla como estado externo. `useSyncExternalStore` en vez de
 * `useState` + `useEffect` porque la primera pintura ya sale con el valor
 * correcto: no hay un fotograma con la disposicion equivocada. Si el entorno no
 * trae `matchMedia` (jsdom), se asume la disposicion ancha.
 */
function usePantallaAncha(): boolean {
  return useSyncExternalStore(
    (avisar) => {
      const consulta = consultaMedia()
      consulta?.addEventListener('change', avisar)
      return () => consulta?.removeEventListener('change', avisar)
    },
    () => consultaMedia()?.matches ?? true,
    () => true,
  )
}

const NUMERO = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 })

function formatear(unidades: number): string {
  return NUMERO.format(unidades)
}

/**
 * El eje de categorias no recorta a media palabra sin avisar: acorta con
 * puntos suspensivos y el nombre completo sigue disponible en el tooltip y en
 * la tabla, que es donde se lee de verdad.
 */
function acortar(nombre: string, tope: number): string {
  if (nombre.length <= tope) return nombre
  return `${nombre.slice(0, tope - 1).trimEnd()}...`
}

interface Fila extends ProductoConsumido {
  /** El valor ya formateado: la etiqueta directa del final de la barra. */
  etiqueta: string
}

interface PropsTooltip {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: Fila }>
}

function TooltipProducto({ active, payload }: PropsTooltip) {
  const fila = payload?.[0]?.payload
  if (!active || !fila) return null

  return (
    <div
      // Ancho acotado: sin el, un nombre largo estira el tooltip mas alla de la
      // tarjeta y aparece un scroll horizontal en movil.
      className="max-w-56 break-words rounded-lg border px-3 py-2 text-sm shadow-sm"
      style={{
        background: TINTA.superficie,
        borderColor: TINTA.borde,
        color: TINTA.primaria,
      }}
    >
      <p className="font-medium">{fila.nombre}</p>
      <p style={{ color: TINTA.secundaria }}>{formatear(fila.unidades)} unidades</p>
    </div>
  )
}

function Grafica({ filas }: { filas: Fila[] }) {
  const alto = ALTO_MARGENES + filas.length * PASO_FILA + ALTO_EJE
  const ancha = usePantallaAncha()
  const medida = ancha ? 'ancha' : 'estrecha'

  return (
    <div
      // La grafica se anuncia como una imagen con su descripcion; el detalle
      // dato a dato lo da la tabla, que es el equivalente accesible.
      role="img"
      aria-label={`${TITULO}: unidades facturadas de ${filas.length} productos en el periodo`}
      style={{ height: alto }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={filas}
          layout="vertical"
          margin={{ top: 8, right: MARGEN_ETIQUETA[medida], bottom: 8, left: 0 }}
          barCategoryGap={PASO_FILA - GROSOR_BARRA}
        >
          {/* Rejilla recesiva: solo las verticales, que son las que ayudan a
              comparar longitudes. Hairline solida, nunca discontinua. */}
          <CartesianGrid horizontal={false} stroke={TINTA.rejilla} strokeWidth={1} />
          <XAxis
            type="number"
            tickFormatter={formatear}
            tickLine={false}
            axisLine={{ stroke: TINTA.eje }}
            tick={{ fill: TINTA.apagada, fontSize: 11 }}
            unit=""
          />
          <YAxis
            type="category"
            dataKey="nombre"
            width={ANCHO_CATEGORIAS[medida]}
            tickFormatter={(nombre: string) => acortar(nombre, CARACTERES_VISIBLES[medida])}
            tickLine={false}
            axisLine={{ stroke: TINTA.eje }}
            tick={{ fill: TINTA.apagada, fontSize: ancha ? 12 : 11 }}
            interval={0}
          />
          <Tooltip
            content={<TooltipProducto />}
            cursor={{ fill: TINTA.rejilla, fillOpacity: 0.6 }}
          />
          {/* Serie unica, asi que no hay leyenda: el titulo ya dice que se mide.
              El extremo de dato va redondeado 4 px y la base cuadrada. */}
          <Bar
            dataKey="unidades"
            name="Unidades"
            fill={TINTA.serie}
            barSize={GROSOR_BARRA}
            radius={[0, 4, 4, 0]}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="etiqueta"
              position="right"
              offset={8}
              fill={TINTA.secundaria}
              fontSize={12}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function TablaProductos({ filas }: { filas: Fila[] }) {
  return (
    <div className="overflow-x-auto">
      <table
        aria-label={`${TITULO}, en unidades facturadas`}
        className="w-full border-collapse text-sm"
      >
        <thead>
          <tr>
            <th
              scope="col"
              className="border-b py-2 pr-4 text-left font-semibold"
              style={{ borderColor: TINTA.eje, color: TINTA.secundaria }}
            >
              Producto
            </th>
            <th
              scope="col"
              className="border-b py-2 text-right font-semibold"
              style={{ borderColor: TINTA.eje, color: TINTA.secundaria }}
            >
              Unidades
            </th>
          </tr>
        </thead>
        <tbody>
          {filas.map((fila) => (
            <tr key={fila.nombre}>
              <th
                scope="row"
                className="border-b py-2 pr-4 text-left font-normal"
                style={{ borderColor: TINTA.rejilla, color: TINTA.primaria }}
              >
                {fila.nombre}
              </th>
              <td
                className="border-b py-2 text-right tabular-nums"
                style={{ borderColor: TINTA.rejilla, color: TINTA.primaria }}
              >
                {fila.etiqueta}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Consumo agregado de los ultimos meses, en barras horizontales.
 *
 * Sobre el boton: en la version Streamlit la grafica se pintaba *dentro* de
 * `if st.button(...)`, asi que solo existia durante la pasada en la que se
 * pulsaba. Cualquier otra interaccion de la pagina -seleccionar un cliente-
 * reejecutaba el script, el boton devolvia False y la grafica desaparecia.
 *
 * Aqui el boton no decide si la grafica existe, sino cuando se pide el dato:
 * enciende una vez la consulta (que recorre las facturas de Holded y es cara) y
 * a partir de ahi manda la cache de TanStack Query. El componente esta montado
 * siempre, asi que nada de lo que pase en el resto de la pagina puede borrarlo.
 */
export function GraficaProductos() {
  const [activada, setActivada] = useState(false)
  const [tablaVisible, setTablaVisible] = useState(false)
  const productos = useProductos({ activo: activada })

  const filas: Fila[] = (productos.data?.items ?? []).map((item) => ({
    ...item,
    etiqueta: formatear(item.unidades),
  }))

  return (
    <section
      aria-labelledby={ID_TITULO}
      className={`${CLASE_GRAFICA} rounded-xl border p-4`}
      style={{ background: TINTA.superficie, borderColor: TINTA.borde }}
    >
      {/* React 19 iza este bloque al head y lo deduplica por `href`. */}
      <style href="viz-paleta" precedence="default">
        {CSS_PALETA}
      </style>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={ID_TITULO} className="text-base font-semibold" style={{ color: TINTA.primaria }}>
            {TITULO}
          </h2>
          <p className="mt-1 text-sm" style={{ color: TINTA.secundaria }}>
            Unidades entregadas en los últimos {productos.data?.meses ?? 3} meses, según los albaranes.
          </p>
        </div>

        {productos.isSuccess && filas.length > 0 ? (
          <button
            type="button"
            onClick={() => setTablaVisible((visible) => !visible)}
            aria-expanded={tablaVisible}
            className="rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
            style={{ borderColor: TINTA.eje, color: TINTA.secundaria }}
          >
            {tablaVisible ? 'Ocultar la tabla' : 'Ver los datos en una tabla'}
          </button>
        ) : null}
      </div>

      <div className="mt-4">
        {!activada ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-6 py-10 text-center" style={{ borderColor: TINTA.eje }}>
            <IconoGrafica aria-hidden="true" className="size-8" style={{ color: TINTA.apagada }} />
            <p className="max-w-sm text-sm" style={{ color: TINTA.secundaria }}>
              El agregado recorre todos los albaranes del periodo y puede tardar unos segundos,
              así que no se pide al abrir la página.
            </p>
            {/* El relleno es el acento del armazon, no `TINTA.serie`. El color
                de serie esta elegido y validado para una barra -una mancha de
                dato-, no para llevar texto encima: con el blanco daba 4,42:1 en
                claro y 3,64:1 en oscuro, por debajo del 4,5:1 que pide un texto.
                Con el acento sale 5,93:1 y 5,26:1. Las barras siguen usando
                `TINTA.serie`, que es para lo que se valido. */}
            <button
              type="button"
              onClick={() => setActivada(true)}
              className="rounded-lg bg-acento px-3 py-2 text-sm font-medium text-tinta-inversa transition-colors hover:bg-acento-fuerte"
            >
              Ver los productos más consumidos
            </button>
          </div>
        ) : productos.isPending ? (
          <Cargando mensaje="Consultando los albaranes" />
        ) : productos.isError ? (
          <EstadoError error={productos.error} onReintentar={() => void productos.refetch()} />
        ) : filas.length === 0 ? (
          <EstadoVacio
            titulo="No hay consumo registrado en el periodo"
            descripcion="No hay ninguna línea de producto en los albaranes de los últimos meses."
          />
        ) : (
          <div className="flex flex-col gap-4">
            <Grafica filas={filas} />
            {tablaVisible ? <TablaProductos filas={filas} /> : null}
          </div>
        )}
      </div>
    </section>
  )
}
